import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import GeneralCalendarsMonthsCalendarMonthRange from 'lumen/components/general/calendars/months/calendar-month-range';
import type { Yearoverview } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

const label = {
  '0': '',
  '1': 'event',
  '2': 'holiday',
  '3': 'both',
};

interface GeneralCalendarsCalendarRangeSignature {
  Args: {
    calendarFocusOnEnd: unknown;
    canSelectMonth?: boolean;
    dateTooltips: unknown;
    displayEvents?: boolean;
    isDisabled?: boolean;
    isFullMonthSelected?: boolean;
    maxDate: Dayjs;
    maxRange?: unknown;
    minDate: Dayjs;
    refocusMonths?: number;
    selectRange:
      | ((range: { moment: { end?: Dayjs; start?: Dayjs } }) => unknown)
      | null;
    selectedRange: {
      end: Dayjs;
      start: Dayjs;
    } | null;
  };
}

export default class GeneralCalendarsCalendarRange extends Component<GeneralCalendarsCalendarRangeSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];

  @tracked eventData = null;

  // See onInsert for more info
  @tracked hasInserted = false;
  @tracked hoveredDayMoment: Dayjs | null = null;
  // The focus month in the calendar
  @tracked month = dayjs();

  numberOfMonthsShown = 3;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNextMonths() {
    return this.args.maxDate.isAfter(
      this.month.add(this.numberOfMonthsShown - 1, 'month'),
      'month',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPreviousMonths() {
    return this.args.minDate.isBefore(this.month, 'month');
  }

  @computed(
    'hoveredDayMoment',
    'args.selectedRange.{start,end}',
    'args.standardRange',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hoveredRange() {
    if (this.hoveredDayMoment) {
      // @ts-expect-error: Incorrect type
      if (this.args.standardRange) {
        return {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          start: this.hoveredDayMoment.startOf(this.args.standardRange),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          end: this.hoveredDayMoment.endOf(this.args.standardRange),
        };
      }

      // @ts-expect-error: Incorrect type
      if (this.args.selectedRange.start) {
        // @ts-expect-error: Incorrect type
        if (this.args.selectedRange.end) {
          return {
            // @ts-expect-error: Incorrect type
            start: this.args.selectedRange.start,
            // @ts-expect-error: Incorrect type
            end: this.args.selectedRange.end,
          };
        }
        if (
          // @ts-expect-error: Incorrect type
          this.args.selectedRange.start.isBefore(this.hoveredDayMoment, 'day')
        ) {
          return {
            // @ts-expect-error: Incorrect type
            start: this.args.selectedRange.start,
            end: this.hoveredDayMoment,
          };
        }
        return {
          start: this.hoveredDayMoment,
          // @ts-expect-error: Incorrect type
          end: this.args.selectedRange.start,
        };
      }
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get months() {
    const months = [];
    for (let i = 0; i < this.numberOfMonthsShown; i++) {
      months.push(this.month.add(i, 'month'));
    }
    return months;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEvents() {
    return this.args.displayEvents && this.access.hasEvents;
  }

  // @ts-expect-error: Incorrect type

  async fetchYearData(years): Promise<void> {
    this.eventData = null;

    const eventData = {};
    const hotelId = this.hotelAndMarketService.hotel?.id;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const promises = years.map(async (year) => {
      const yearOverview = this.store.peekRecord<Yearoverview>(
        'yearoverview',
        `${hotelId},${year}`,
      );

      // @ts-expect-error: Incorrect type
      let importantDays = [];

      if (yearOverview) {
        importantDays = yearOverview.importantDays;
      } else {
        const yearOverviews = await this.store.query<Yearoverview>(
          'yearoverview',
          {
            hotel_id: hotelId,
            namespace: APIGATEWAY,
            typeKey: 'events/yearoverview',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            year,
          },
        );

        if (yearOverviews[0]) {
          importantDays = yearOverviews[0].importantDays;
        }
      }

      // @ts-expect-error: Incorrect type
      importantDays.forEach(({ day, label: labelId }) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        eventData[day] = label[labelId] ?? '';
      });
    });

    await Promise.allSettled(promises);

    // @ts-expect-error: Incorrect type
    this.eventData = eventData;
  }

  @action
  fetchYearDataForMonths(): void {
    // @ts-expect-error: Incorrect type
    const firstYear = this.months[0].format('YYYY');
    // @ts-expect-error: Incorrect type
    const lastYear = this.months[this.months.length - 1].format('YYYY');
    if (firstYear === lastYear) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchYearData([firstYear]);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchYearData([firstYear, lastYear]);
    }
  }

  @action
  nextClicked(): void {
    if (this.hasNextMonths) {
      this.month = dayjs(this.month, 'MM-YYYY').add(3, 'month');

      if (this.showEvents) {
        this.fetchYearDataForMonths();
      }
    }
  }

  @action
  onInsert(): void {
    this.hasInserted = true;

    if (this.args.calendarFocusOnEnd) {
      // By default we look at the end of the range but if the range consists of a single day, it only has a start.
      const referenceDay =
        // @ts-expect-error: Incorrect type
        this.args.selectedRange.end || this.args.selectedRange.start;
      // if we focus on the end, shift the months so the last one is at the end of the range
      this.month = referenceDay.subtract(this.numberOfMonthsShown - 1, 'month');
    } else {
      // @ts-expect-error: Incorrect type
      this.month = this.args.selectedRange.start;
    }
    if (this.showEvents) {
      this.fetchYearDataForMonths();
    }
  }

  @action
  previousClicked(): void {
    if (this.hasPreviousMonths) {
      this.month = dayjs(this.month, 'MM-YYYY').subtract(3, 'month');

      if (this.showEvents) {
        this.fetchYearDataForMonths();
      }
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  updateHoveredRange(day): void {
    if (day) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.hoveredDayMoment = dayjs(day.date);
    } else {
      this.hoveredDayMoment = null;
    }
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class="flex justify-center"
      {{didInsert this.onInsert}}
      {{didUpdate this.onInsert @refocusMonths}}
    >
      {{#if this.hasInserted}}
        <div class="arrow-wrapper">
          <div
            class="switchmonth switch-to-previous-month
              {{unless this.hasPreviousMonths 'disabled'}}"
            role="button"
            {{on "click" this.previousClicked}}
          >
            <PrismIcon @name="chevron-left" />
          </div>
        </div>
        <div class="months-container">
          {{#each this.months as |month|}}
            <GeneralCalendarsMonthsCalendarMonthRange
              @canSelectMonth={{@canSelectMonth}}
              @dateTooltips={{@dateTooltips}}
              @eventData={{this.eventData}}
              @highlightPeriod={{this.updateHoveredRange}}
              @hoveredDayMoment={{this.hoveredDayMoment}}
              @hoveredRange={{this.hoveredRange}}
              @isDisabled={{@isDisabled}}
              {{! @glint-expect-error: Incorrect type }}
              @isFullMonthSelected={{@isFullMonthSelected}}
              @maxDate={{@maxDate}}
              @maxRange={{@maxRange}}
              @minDate={{@minDate}}
              @month={{month}}
              @monthRange={{@selectedRange}}
              @selectRange={{@selectRange}}
            />
          {{/each}}
        </div>
        <div class="arrow-wrapper">
          <div
            class="switchmonth switch-to-next-month
              {{unless this.hasNextMonths 'disabled'}}"
            role="button"
            {{on "click" this.nextClicked}}
          >
            <PrismIcon @name="chevron-right" />
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
